/**
 * Authentication Context
 * Manages user authentication state across the app
 * Ensures credentials are properly fetched and persisted
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userAuthService, User } from '../services/userAuthService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();

    // Listen for auth state changes
    const authListener = userAuthService.onAuthStateChange((newUser) => {
      setUser(newUser);
      setLoading(false);
    });

    return () => {
      if (authListener && typeof (authListener as any).unsubscribe === 'function') {
        (authListener as any).unsubscribe();
      }
    };
  }, []);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      const currentUser = await userAuthService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error initializing auth:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const currentUser = await userAuthService.getCurrentUser();
      setUser(currentUser);

      // Sync localStorage cart to database when user signs in
      if (currentUser) {
        const { customSafariService } = await import('../services/customSafariService');
        await customSafariService.syncLocalStorageCartToDatabase(currentUser.id);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    }
  };

  const signOut = async () => {
    try {
      await userAuthService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        refreshUser,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

